import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Order } from '../types';
import { FirestoreService } from '../firebase/firestore';

interface OrderContextType {
  orders: Order[];
  addOrder: (order: Order) => Promise<Order>;
  updateOrderStatus: (orderId: string, status: Order['status']) => Promise<void>;
  deleteOrder: (orderId: string) => Promise<void>;
  getCustomerOrders: (customerId: string) => Order[];
  getOrderById: (orderId: string) => Order | undefined;
  isLoading?: boolean;
}

const OrderContext = createContext<OrderContextType | undefined>(undefined);

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context === undefined) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

interface OrderProviderProps {
  children: ReactNode;
}

export const OrderProvider: React.FC<OrderProviderProps> = ({ children }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load orders from Firestore on mount and set up real-time listener
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const setupRealtimeListener = async () => {
      try {
        console.log('🔄 OrderContext: Setting up real-time listener for orders...');
        setIsLoading(true);
        
        // Set up real-time listener
        unsubscribe = await FirestoreService.subscribeToOrders((firestoreOrders) => {
          console.log('📦 OrderContext: Real-time update - Orders from Firestore:', firestoreOrders.length);
          
          if (firestoreOrders.length > 0) {
            setOrders(firestoreOrders);
            localStorage.setItem('bookfood_orders', JSON.stringify(firestoreOrders));
            console.log('✅ OrderContext: Orders updated from real-time listener');
          } else {
            console.log('⚠️ OrderContext: No orders in Firestore');
            setOrders([]);
          }
          
          setIsLoading(false);
        });
        
        console.log('✅ OrderContext: Real-time listener set up successfully');
      } catch (error) {
        console.error('❌ OrderContext: Error setting up real-time listener:', error);
        
        // Fallback to one-time load
        try {
          const firestoreOrders = await FirestoreService.getOrders();
          if (firestoreOrders.length > 0) {
            setOrders(firestoreOrders);
            localStorage.setItem('bookfood_orders', JSON.stringify(firestoreOrders));
          } else {
            const savedOrders = localStorage.getItem('bookfood_orders');
            if (savedOrders) {
              setOrders(JSON.parse(savedOrders));
            }
          }
        } catch (loadError) {
          console.error('❌ OrderContext: Error loading orders:', loadError);
          const savedOrders = localStorage.getItem('bookfood_orders');
          if (savedOrders) {
            setOrders(JSON.parse(savedOrders));
          }
        }
        
        setIsLoading(false);
      }
    };

    setupRealtimeListener();

    // Cleanup listener on unmount
    return () => {
      if (unsubscribe) {
        console.log('🔌 OrderContext: Unsubscribing from real-time listener');
        unsubscribe();
      }
    };
  }, []);

  const addOrder = async (orderData: Order) => {
    try {
      console.log('💾 OrderContext: Saving order to Firestore...', orderData);
      const savedOrder = await FirestoreService.addOrder(orderData);
      
      if (savedOrder) {
        console.log('✅ OrderContext: Order saved to Firestore with ID:', savedOrder.id);
        const newOrders = [savedOrder, ...orders];
        setOrders(newOrders);
        
        // Also save to localStorage as backup
        localStorage.setItem('bookfood_orders', JSON.stringify(newOrders));
        console.log('💾 OrderContext: Order also saved to localStorage');
        
        return savedOrder;
      } else {
        console.error('❌ OrderContext: Failed to save order to Firestore');
        // Fallback to local only
        const newOrders = [orderData, ...orders];
        setOrders(newOrders);
        localStorage.setItem('bookfood_orders', JSON.stringify(newOrders));
        return orderData;
      }
    } catch (error) {
      console.error('❌ OrderContext: Error saving order:', error);
      // Fallback to local only
      const newOrders = [orderData, ...orders];
      setOrders(newOrders);
      localStorage.setItem('bookfood_orders', JSON.stringify(newOrders));
      return orderData;
    }
  };

  const updateOrderStatus = async (orderId: string, status: Order['status']) => {
    try {
      console.log('💾 OrderContext: Updating order status in Firestore...', orderId, status);
      
      const order = orders.find(o => o.id === orderId);
      
      // If cancelling an order, restore stock first
      if (status === 'cancelled' && order) {
        const stockRestoreItems = order.items.map(item => ({
          id: item.id,
          quantity: item.quantity
        }));
        await restoreProductStock(stockRestoreItems);
      }
      
      const success = await FirestoreService.updateOrder(orderId, { status });
      
      if (success) {
        console.log('✅ OrderContext: Order status updated in Firestore');
        setOrders(prev => 
          prev.map(order => 
            order.id === orderId ? { ...order, status } : order
          )
        );
      } else {
        console.error('❌ OrderContext: Failed to update order status in Firestore');
        // Fallback to local only
        setOrders(prev => 
          prev.map(order => 
            order.id === orderId ? { ...order, status } : order
          )
        );
      }
    } catch (error) {
      console.error('❌ OrderContext: Error updating order status:', error);
      // Fallback to local only
      setOrders(prev => 
        prev.map(order => 
          order.id === orderId ? { ...order, status } : order
        )
      );
    }
  };

  const deleteOrder = async (orderId: string) => {
    try {
      console.log('🗑️ OrderContext: Deleting order from Firestore...', orderId);
      const success = await FirestoreService.deleteOrder(orderId);
      
      if (success) {
        console.log('✅ OrderContext: Order deleted from Firestore');
        setOrders(prev => prev.filter(order => order.id !== orderId));
      } else {
        console.error('❌ OrderContext: Failed to delete order from Firestore');
        // Fallback to local only
        setOrders(prev => prev.filter(order => order.id !== orderId));
      }
    } catch (error) {
      console.error('❌ OrderContext: Error deleting order:', error);
      // Fallback to local only
      setOrders(prev => prev.filter(order => order.id !== orderId));
    }
  };

  const getCustomerOrders = (customerId: string) => {
    console.log('🔍 OrderContext: Filtering orders for customerId:', customerId);
    console.log('🔍 OrderContext: Total orders available:', orders.length);
    console.log('🔍 OrderContext: Orders with customerIds:', orders.map(o => ({ id: o.id, customerId: o.customerId, customerName: o.customerName })));
    const filteredOrders = orders
      .filter(order => order.customerId === customerId)
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Sort by date descending (most recent first)
    console.log('🔍 OrderContext: Filtered orders found:', filteredOrders.length);
    return filteredOrders;
  };

  const getOrderById = (orderId: string) => {
    return orders.find(order => order.id === orderId);
  };

  const restoreProductStock = async (orderItems: { id: string; quantity: number }[]) => {
    console.log('📦 OrderContext: Restoring product stock for cancelled order items:', orderItems);
    
    try {
      for (const orderItem of orderItems) {
        console.log(`💾 OrderContext: Restoring stock for product ${orderItem.id}, quantity: ${orderItem.quantity}`);
        // Get current product stock from Firestore
        const products = await FirestoreService.getProducts();
        const product = products.find(p => p.id === orderItem.id);
        
        if (product) {
          const newStock = product.stock + orderItem.quantity;
          console.log(`💾 OrderContext: Updating stock in Firestore for ${product.name} from ${product.stock} to ${newStock}`);
          await FirestoreService.updateProduct(orderItem.id, { stock: newStock });
        }
      }
      console.log('✅ OrderContext: Stock restored successfully in Firestore');
    } catch (error) {
      console.error('❌ OrderContext: Error restoring stock in Firestore:', error);
    }
  };


  const value = {
    orders,
    addOrder,
    updateOrderStatus,
    deleteOrder,
    getCustomerOrders,
    getOrderById,
    isLoading
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};
